import { faComputer, faFileSpreadsheet, faPlus } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { message, Popconfirm, Skeleton, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomButton from "../common/CustomButton";
import { fromUtc } from "../helpers/dayjsSetup";
import UserService from "../services/User/UserService";
import AddEditFormComponent from "./AddEditFormComponent";
import ImportDrawer from "./ImportDrawer";

const UserTableComponent = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [open, setOpenAddDrawer] = useState(false);
  const [type, setType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const location = useLocation();
  const { supplierID } = location.state || {};
  const [list, setList] = useState({
    request: {
      firstName: null,
      lastName: null,
      email: null,
      role: null,
      active: null,
      orgID: supplierID,
      pageFilter: { all: false, size: 10, num: 0 },
      sortFilter: { field: "", order: "" },
    },
    response: { total: 0, data: [] },
  });

  useEffect(() => {
    fetchUsers(list.request);
  }, []);

  const handleTableChange = (pagination, filters, sorter) => {
    const updatedRequest = {
      ...list.request,
      sortFilter: {
        field: sorter.field || "",
        order:
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
              ? "desc"
              : "",
      },
      pageFilter: {
        ...list.request.pageFilter,
        size: pagination.pageSize,
        num: pagination.current - 1,
      },
    };

    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchUsers(updatedRequest);
  };

  const handleAddUser = (values) => {
    if (type === "edit") {
      setDataSource((prevData) =>
        prevData.data.map((item) =>
          item.key === editingUser.key ? { ...item, ...values } : item
        )
      );
    } else {
      const newUser = { key: Date.now(), ...values };
      setDataSource((prevDataSource) => ({
        ...prevDataSource,
        data: [...prevDataSource.data, newUser],
        total: prevDataSource.total + 1,
      }));
    }
    setOpenAddDrawer(false);
  };

  const handleStatusChange = async (id) => {
    const record = dataSource.data.find((item) => item.id === id);
    if (record) {
      const request = { ...record, isActive: !record.isActive };
      const response = await UserService.updateUser(request);
      if (response?.status === 200) {
        message.success(`Status has been updated to ${request.isActive ? "Active" : "Inactive"}`);
        fetchUsers(list.request);
      } else {
        message.error("Something went wrong");
      }
    }
  };

  const fetchUsers = (request) => {
    setLoading(true);

    UserService.fetchUsers(request)
      .then((response) => {
        setDataSource(response.data);
      })
      .catch((err) => {
        message(err.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const getRowSelection = () => ({
    selectedRowKeys,
    onChange: onSelectChange,
  });

  const columns = [
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      align: "center",
      sorter: false,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      align: "center",
      sorter: false,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center",
      sorter: false,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      align: "center",
      sorter: false,
      render: (text) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : text ? (
          fromUtc(text).format("DD MMM YYYY")
        ) : (
          "NA"
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (status, record) => {
        if (isLoading) {
          return <Skeleton active size="small" />;
        }
        return (
          <Popconfirm
            title={`Are you sure you want to change the status to ${status ? "Inactive" : "Active"}?`}
            onConfirm={() => handleStatusChange(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tag
              className={`hover:cursor-pointer hover:!text-white ${status
                ? "text-primary border-primary hover:bg-primary"
                : "text-red-500 border-red-500 hover:bg-red-500"
                }`}
            >
              {status ? "Active" : "Inactive"}
            </Tag>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <>
      {loading ? (
        <Skeleton active size="small" />
      ) : (
        <div>
          <div className="flex justify-end gap-x-4">
            <CustomButton
              className="rounded-lg"
              type="primary"
              onClick={() => {
                setOpenAddDrawer(true);
                setType("add");
                setEditingUser(null);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add
            </CustomButton>
            <CustomButton
              type="secondary"
              className="flex items-center rounded-lg"
              onClick={() => setOpenImport(true)}
            >
              <FontAwesomeIcon icon={faComputer} />
              Import
            </CustomButton>
            <CustomButton
              className="flex items-center rounded-lg"
              type="tertiary"
            >
              <FontAwesomeIcon icon={faFileSpreadsheet} />
              Export
            </CustomButton>
          </div>
          <Table
            className="mt-2 border-2 border-gray-300 rounded-lg"
            // rowSelection={getRowSelection()}
            columns={columns}
            pagination={false}
            dataSource={
              isLoading
                ? new Array(10).fill(null).map((_, index) => ({ key: index }))
                : dataSource?.data?.map((item, index) => ({
                  ...item,
                  key: index,
                }))
            }
            onChange={handleTableChange}
            bordered={false}
            rowClassName="relative shadow-lg before:absolute before:left-0 before:right-0 before:top-[-4px] before:shadow-md after:absolute after:left-0 after:right-0 after:bottom-[-4px] after:shadow-md"
          />
          <AddEditFormComponent
            open={open}
            onClose={() => setOpenAddDrawer(false)}
            onSubmit={handleAddUser}
            initialValues={editingUser}
            type={type}
            orgID={supplierID}
            onSuccess={() => {
              const { request } = list;
              fetchUsers(request);
            }}
            formType="user"
          />
          <ImportDrawer
            open={openImport}
            onClose={() => setOpenImport(false)}
            type={"UserImport"}
          />
        </div>
      )}
    </>
  );
};

export default UserTableComponent;
